import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  TwoWheeler as TwoWheelerIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  authService,
  deliveryCourierService,
  type DeliveryCourier,
  type DeliveryCourierVehicleType,
  type ListDeliveryCouriersParams,
} from '../services';
import { useToast } from '../hooks/useToast';

type StatusFilter = 'all' | 'active' | 'inactive';

interface CourierFormState {
  name: string;
  phone: string;
  document: string;
  vehicleType: DeliveryCourierVehicleType | '';
  vehiclePlate: string;
  notes: string;
  isActive: boolean;
}

const EMPTY_FORM: CourierFormState = {
  name: '',
  phone: '',
  document: '',
  vehicleType: '',
  vehiclePlate: '',
  notes: '',
  isActive: true,
};

const VEHICLE_TYPE_OPTIONS: { value: DeliveryCourierVehicleType; label: string }[] = [
  { value: 'MOTO', label: 'Moto' },
  { value: 'CARRO', label: 'Carro' },
  { value: 'BICICLETA', label: 'Bicicleta' },
  { value: 'OUTRO', label: 'Outro' },
];

const sanitizeDigits = (value: string): string => value.replace(/\D/g, '');

const formatPhone = (digits: string | null | undefined): string => {
  if (!digits) {
    return '';
  }

  const value = sanitizeDigits(digits).slice(0, 11);
  if (value.length <= 2) return value;
  if (value.length <= 6) return `(${value.slice(0, 2)}) ${value.slice(2)}`;
  if (value.length === 10) {
    return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
  }
  return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7)}`;
};

const formatDocument = (digits: string | null | undefined): string => {
  if (!digits) return '';
  const value = sanitizeDigits(digits).slice(0, 14);
  if (value.length <= 11) {
    const padded = value.padEnd(11, '');
    if (padded.length <= 3) return padded;
    if (padded.length <= 6) return `${padded.slice(0, 3)}.${padded.slice(3)}`;
    if (padded.length <= 9) return `${padded.slice(0, 3)}.${padded.slice(3, 6)}.${padded.slice(6)}`;
    return `${padded.slice(0, 3)}.${padded.slice(3, 6)}.${padded.slice(6, 9)}-${padded.slice(9, 11)}`;
  }
  return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5, 8)}/${value.slice(8, 12)}-${value.slice(12, 14)}`;
};

const formatVehiclePlate = (plate: string): string => plate.toUpperCase().slice(0, 7);

const formatVehicleTypeLabel = (type: DeliveryCourierVehicleType | null | undefined): string => {
  if (!type) return '—';
  const option = VEHICLE_TYPE_OPTIONS.find((item) => item.value === type);
  return option?.label ?? type;
};

const DeliveryCouriersPage: React.FC = () => {
  const [couriers, setCouriers] = useState<DeliveryCourier[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{ searchTerm: string; status: StatusFilter; cnpj: string }>(
    { searchTerm: '', status: 'all', cnpj: '' },
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [formState, setFormState] = useState<CourierFormState>(EMPTY_FORM);
  const [editingCourier, setEditingCourier] = useState<DeliveryCourier | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeliveryCourier | null>(null);
  const { showError, showSuccess, showInfo } = useToast();

  const userRole = authService.getUserRole();
  const isDeveloper = userRole === 'DEVELOPER';

  const loadCouriers = useCallback(
    async (overrides?: Partial<ListDeliveryCouriersParams>) => {
      try {
        setLoading(true);
        const params = {
          searchTerm: filters.searchTerm.trim() || undefined,
          isActive:
            filters.status === 'all'
              ? undefined
              : filters.status === 'active'
              ? true
              : false,
          cnpj: isDeveloper ? filters.cnpj.trim() || undefined : undefined,
          ...overrides,
        };

        const data = await deliveryCourierService.list(params);
        setCouriers(data);
      } catch (error: any) {
        console.error('[DeliveryCouriersPage] loadCouriers error', error);
        showError(error.message || 'Não foi possível carregar os entregadores.');
      } finally {
        setLoading(false);
      }
    },
    [filters.cnpj, filters.searchTerm, filters.status, isDeveloper, showError],
  );

  useEffect(() => {
    loadCouriers();
  }, [loadCouriers]);

  const handleOpenDialog = (courier?: DeliveryCourier) => {
    if (courier) {
      setEditingCourier(courier);
      setFormState({
        name: courier.name,
        phone: courier.phone ?? '',
        document: courier.document ?? '',
        vehicleType: courier.vehicleType ?? '',
        vehiclePlate: courier.vehiclePlate ?? '',
        notes: courier.notes ?? '',
        isActive: courier.isActive,
      });
    } else {
      setEditingCourier(null);
      setFormState(EMPTY_FORM);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (dialogLoading) return;
    setDialogOpen(false);
    setEditingCourier(null);
    setFormState(EMPTY_FORM);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formState.name.trim()) {
      showInfo('Informe o nome do entregador.');
      return;
    }

    const phoneDigits = formState.phone ? sanitizeDigits(formState.phone) : undefined;
    const documentDigits = formState.document ? sanitizeDigits(formState.document) : undefined;
    const vehiclePlateValue = formState.vehiclePlate ? formatVehiclePlate(formState.vehiclePlate) : undefined;
    const notesValue = formState.notes.trim() ? formState.notes.trim() : undefined;

    const payload = {
      name: formState.name,
      phone: phoneDigits ?? undefined,
      document: documentDigits ?? undefined,
      vehicleType: formState.vehicleType || undefined,
      vehiclePlate: vehiclePlateValue,
      notes: notesValue,
      isActive: formState.isActive,
      cnpj: isDeveloper ? filters.cnpj.trim() || editingCourier?.cnpj || undefined : undefined,
    };

    try {
      setDialogLoading(true);
      if (editingCourier) {
        const updated = await deliveryCourierService.update(editingCourier.id, payload);
        setCouriers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        showSuccess('Entregador atualizado com sucesso.');
      } else {
        const created = await deliveryCourierService.create(payload);
        setCouriers((prev) => [created, ...prev]);
        showSuccess('Entregador cadastrado com sucesso.');
      }
      handleCloseDialog();
    } catch (error: any) {
      console.error('[DeliveryCouriersPage] handleSubmit error', error);
      showError(error.message || 'Não foi possível salvar o entregador.');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      await deliveryCourierService.delete(
        deleteTarget.id,
        isDeveloper ? filters.cnpj.trim() || deleteTarget.cnpj || undefined : undefined,
      );
      setCouriers((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      showSuccess('Entregador removido com sucesso.');
    } catch (error: any) {
      console.error('[DeliveryCouriersPage] handleDelete error', error);
      showError(error.message || 'Não foi possível remover o entregador.');
    } finally {
      setDeleteTarget(null);
      setDeleteDialogOpen(false);
    }
  };

  const filteredCouriers = useMemo(() => couriers, [couriers]);

  const filtersActive = Boolean(
    filters.searchTerm.trim() || filters.status !== 'all' || (isDeveloper && filters.cnpj.trim()),
  );

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
            Entregadores
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 620 }}>
            Cadastre entregadores, defina dados de contato e organize os responsáveis por cada entrega.
            Utilize os filtros para localizar rapidamente e mantenha o cadastro sempre atualizado.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Novo entregador
        </Button>
      </Box>

      <Paper elevation={3} sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-end">
          <TextField
            label="Buscar por nome ou telefone"
            value={filters.searchTerm}
            onChange={(event) =>
              setFilters((prev) => ({ ...prev, searchTerm: event.target.value }))
            }
            fullWidth
            InputProps={{ endAdornment: <SearchIcon fontSize="small" color="action" /> }}
          />

          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filters.status}
              label="Status"
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, status: event.target.value as StatusFilter }))
              }
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Ativos</MenuItem>
              <MenuItem value="inactive">Inativos</MenuItem>
            </Select>
          </FormControl>

          {isDeveloper && (
            <TextField
              label="CNPJ alvo"
              value={filters.cnpj}
              onChange={(event) =>
                setFilters((prev) => ({ ...prev, cnpj: sanitizeDigits(event.target.value).slice(0, 14) }))
              }
              placeholder="Somente números"
              inputProps={{ inputMode: 'numeric', pattern: '\\d*' }}
              sx={{ minWidth: 220 }}
            />
          )}

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={() => loadCouriers()}
            >
              Atualizar
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({ searchTerm: '', status: 'all', cnpj: '' });
                loadCouriers({ searchTerm: undefined, isActive: undefined, cnpj: undefined });
              }}
              disabled={!filtersActive}
            >
              Limpar filtros
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{filteredCouriers.length} entregador(es)</Typography>
          {loading && <CircularProgress size={22} />}
        </Box>
        <Divider />
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>Veículo</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Atualização</TableCell>
                {isDeveloper && <TableCell>CNPJ</TableCell>}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCouriers.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={isDeveloper ? 8 : 7}>
                    <Box textAlign="center" py={5}>
                      <TwoWheelerIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                      <Typography variant="body1" color="text.secondary">
                        Nenhum entregador encontrado com os filtros atuais.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {filteredCouriers.map((courier) => (
                <TableRow hover key={courier.id}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {courier.name}
                    </Typography>
                    {courier.notes && (
                      <Typography variant="caption" color="text.secondary">
                        {courier.notes}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatPhone(courier.phone)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDocument(courier.document)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatVehicleTypeLabel(courier.vehicleType)}</Typography>
                    {courier.vehiclePlate && (
                      <Typography variant="caption" color="text.secondary">
                        Placa: {courier.vehiclePlate.toUpperCase()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={courier.isActive ? 'success' : 'default'}
                      label={courier.isActive ? 'Ativo' : 'Inativo'}
                    />
                  </TableCell>
                  <TableCell>{format(new Date(courier.updatedAt), 'dd/MM/yyyy HH:mm')}</TableCell>
                  {isDeveloper && <TableCell>{courier.cnpj || '—'}</TableCell>}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Editar">
                        <IconButton color="primary" onClick={() => handleOpenDialog(courier)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remover">
                        <IconButton
                          color="error"
                          onClick={() => {
                            setDeleteTarget(courier);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm" component="form" onSubmit={handleSubmit}>
        <DialogTitle>{editingCourier ? 'Editar entregador' : 'Novo entregador'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              required
              value={formState.name}
              onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
            />
            <TextField
              label="Telefone"
              placeholder="(00) 00000-0000"
              value={formatPhone(formState.phone)}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, phone: sanitizeDigits(event.target.value) }))
              }
              inputProps={{ inputMode: 'tel' }}
            />
            <TextField
              label="Documento"
              placeholder="CPF ou CNPJ"
              value={formatDocument(formState.document)}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, document: sanitizeDigits(event.target.value) }))
              }
              inputProps={{ inputMode: 'numeric', pattern: '[0-9.\\-/]*' }}
            />
            <FormControl fullWidth>
              <InputLabel id="vehicle-type-label">Tipo de veículo</InputLabel>
              <Select
                labelId="vehicle-type-label"
                label="Tipo de veículo"
                value={formState.vehicleType}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, vehicleType: event.target.value as CourierFormState['vehicleType'] }))
                }
                displayEmpty
              >
                <MenuItem value="">
                  <em>Não informado</em>
                </MenuItem>
                {VEHICLE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Placa do veículo"
              placeholder="ABC1D23"
              value={formState.vehiclePlate.toUpperCase()}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, vehiclePlate: formatVehiclePlate(event.target.value) }))
              }
              inputProps={{ maxLength: 7, style: { textTransform: 'uppercase' } }}
            />
            <TextField
              label="Observações"
              multiline
              minRows={3}
              value={formState.notes}
              onChange={(event) => setFormState((prev) => ({ ...prev, notes: event.target.value }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formState.isActive}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, isActive: event.target.checked }))
                  }
                />
              }
              label="Entregador ativo"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={dialogLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={dialogLoading}>
            {editingCourier ? 'Salvar alterações' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remover entregador</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Tem certeza de que deseja remover o entregador{' '}
            <strong>{deleteTarget?.name}</strong>? Essa ação não poderá ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DeliveryCouriersPage;
